import {
  useState, useEffect 
} from 'react';
import {
  useParams, useNavigate 
} from 'react-router-dom';
import {
  ArrowLeft, Gavel 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useAvailableSourcingEvents, useSubmitBid 
} from '@/hooks/use-sourcing';
import { AuctionStatusWidget } from '@/features/sourcing';
import {
  LineItemsCard,
  AdditionalInfoCard,
  EventDetailsCard,
  BidSummaryCard,
  type BidLineInput,
} from './components';

export function SubmitBidPage(): React.JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const {
    data: eventsData, isLoading 
  } = useAvailableSourcingEvents();
  const submitBid = useSubmitBid();

  const [bidLines, setBidLines] = useState<BidLineInput[]>([]);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');

  const event = eventsData?.data.find((e) => e.eventId === eventId);

  useEffect(() => {
    if (event) {
      setBidLines(
        event.lines.map((line) => ({
          lineId: line.lineId,
          itemId: line.itemId,
          itemName: line.itemName,
          quantity: line.quantity,
          unit: line.unit,
          unitPrice: '',
          leadTimeDays: '2',
          notes: '',
        }))
      );
      const defaultValidUntil = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
      setValidUntil(defaultValidUntil.toISOString().split('T')[0]);
    }
  }, [event]);

  const handleLineChange = (
    index: number,
    field: keyof BidLineInput,
    value: string
  ): void => {
    setBidLines((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value 
      };
      return updated;
    });
  };

  const calculateTotal = (): number => {
    return bidLines.reduce((sum, line) => {
      const price = parseFloat(line.unitPrice) || 0;
      const eventLine = event?.lines.find((l) => l.lineId === line.lineId);
      return sum + price * (eventLine?.quantity ?? 0);
    }, 0);
  };

  const handleSubmit = (): void => {
    if (!eventId) return;

    const lines = bidLines.map((line) => {
      const eventLine = event?.lines.find((l) => l.lineId === line.lineId);
      const unitPrice = parseFloat(line.unitPrice) || 0;
      const quantity = eventLine?.quantity ?? 0;
      return {
        lineId: line.lineId,
        itemId: line.itemId,
        unitPrice,
        totalPrice: unitPrice * quantity,
        leadTimeDays: parseInt(line.leadTimeDays) || 2,
        notes: line.notes || undefined,
      };
    });

    submitBid.mutate(
      {
        eventId,
        lines,
        validUntil: new Date(validUntil).toISOString(),
        notes: notes || undefined,
      },
      { onSuccess: () => navigate('/sourcing/bids') }
    );
  };

  const hasAllPrices = bidLines.every((line) => parseFloat(line.unitPrice) > 0);
  const isValid = hasAllPrices && validUntil.length > 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse bg-muted rounded" />
        <Card className="h-96 animate-pulse bg-muted" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/sourcing/available')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium">Event not found</p>
            <p className="text-muted-foreground">
              This sourcing event may have been closed or removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: event.currency,
    }).format(amount);

  const isReverseAuction = event.eventType === 'reverse_auction';
  const bidTotal = calculateTotal();

  return (
    <div className="space-y-6">
      <PageHeader
        event={event}
        isReverseAuction={isReverseAuction}
        onBack={() => navigate('/sourcing/available')}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <LineItemsCard
            bidLines={bidLines}
            event={event}
            formatCurrency={formatCurrency}
            onLineChange={handleLineChange}
          />
          <AdditionalInfoCard
            validUntil={validUntil}
            notes={notes}
            onValidUntilChange={setValidUntil}
            onNotesChange={setNotes}
          />
          {isReverseAuction && (
            <AuctionStatusWidget
              event={event}
              yourBidTotal={bidTotal}
              formatCurrency={formatCurrency}
            />
          )}
        </div>

        <div className="space-y-4">
          <EventDetailsCard
            event={event}
            formatCurrency={formatCurrency}
            isReverseAuction={isReverseAuction}
          />
          <BidSummaryCard
            bidLines={bidLines}
            bidTotal={bidTotal}
            event={event}
            isReverseAuction={isReverseAuction}
            isValid={isValid}
            isPending={submitBid.isPending}
            formatCurrency={formatCurrency}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}

interface PageHeaderProps {
  readonly event: {
    title: string;
    eventNumber: string 
  };
  readonly isReverseAuction: boolean;
  readonly onBack: () => void;
}

function PageHeader({
  event, isReverseAuction, onBack 
}: PageHeaderProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">Submit Bid</h1>
          {isReverseAuction && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium flex items-center gap-1">
              <Gavel className="h-3 w-3" />
              Live Auction
            </span>
          )}
        </div>
        <p className="text-muted-foreground">
          {event.title} - {event.eventNumber}
        </p>
      </div>
    </div>
  );
}
